import type { Editor } from "@tiptap/react";
import type { ComponentProps } from "react";

import clsx from "clsx";

import BoldIcon from "./icons/bold.svg?react";
import BulletListIcon from "./icons/bullet-list.svg?react";
import ItalicIcon from "./icons/italic.svg?react";
import LinkIcon from "./icons/link.svg?react";
import OrderedListIcon from "./icons/ordered-list.svg?react";
import StrikeIcon from "./icons/strike.svg?react";
import UnderlineIcon from "./icons/underline.svg?react";

type MenuBarButtonProps = {
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
} & ComponentProps<"button">;

function MenuBarButton({
  icon,
  isActive,
  onClick,
  ...buttonProps
}: MenuBarButtonProps) {
  return (
    <button
      className={clsx(
        isActive && "bg-bg-primary-hover text-wysiwyg-editor-icon-fg-active",
        "size-4xl overflow-hidden rounded-sm text-wysiwyg-editor-icon-fg hover:bg-bg-primary-hover",
      )}
      onClick={onClick}
      type="button"
      {...buttonProps}
    >
      {icon}
    </button>
  );
}

type MenuBarProps = {
  disabled: boolean;
  editor: Editor;
};

export function MenuBar({ disabled, editor }: MenuBarProps) {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex gap-xs rounded-t-md border-b border-border-secondary bg-bg-primary p-md">
      <MenuBarButton
        aria-label="Bold"
        disabled={disabled}
        icon={<BoldIcon />}
        isActive={!disabled && editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <MenuBarButton
        aria-label="Italic"
        disabled={disabled}
        icon={<ItalicIcon />}
        isActive={!disabled && editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <MenuBarButton
        aria-label="Underline"
        disabled={disabled}
        icon={<UnderlineIcon />}
        isActive={!disabled && editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <MenuBarButton
        aria-label="Strike"
        disabled={disabled}
        icon={<StrikeIcon />}
        isActive={!disabled && editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <MenuBarButton
        aria-label="Link"
        disabled={disabled}
        icon={<LinkIcon />}
        isActive={!disabled && editor.isActive("link")}
        onClick={() => {
          if (editor.isActive("link")) {
            editor.chain().focus().unsetLink().run();
          } else {
            if (!editor.view.state.selection.empty) {
              const link = window.prompt("Enter the URL") || "";

              if (link) {
                editor.chain().focus().setLink({ href: link }).run();
              }
            }
          }
        }}
      />
      <MenuBarButton
        aria-label="Bullet list"
        disabled={disabled}
        icon={<BulletListIcon />}
        isActive={!disabled && editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <MenuBarButton
        aria-label="Ordered list"
        disabled={disabled}
        icon={<OrderedListIcon />}
        isActive={!disabled && editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
    </div>
  );
}
